import { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.svg';
import LoginButton from './Auth/LoginButton';
import { useAuth } from './Auth/AuthContext';
import { useSubscription } from './Auth/SubscriptionContext';
import SubscriptionManagementModal from './Auth/SubscriptionManagementModal';

function Navbar({ showBackButton, onBackClick }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { isAuthenticated } = useAuth();
  const { subscription } = useSubscription();

  const handleManageSubscription = () => {
    setShowSubscriptionModal(true);
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-white/70 border-b border-purple-100/30 backdrop-blur-md z-50">
      <div className="w-full">
        <div className="h-16 flex items-center justify-between max-w-[1920px] mx-auto px-4 sm:px-12">
          {/* Left section */}
          <div className="flex items-center">
            {showBackButton ? (
              <button
                onClick={onBackClick}
                className="px-4 py-2 bg-white/80 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors border border-gray-200 font-space"
              >
                ← New Search
              </button>
            ) : (
              <Link to="/">
                <img src={logo} alt="Moodshelf" className="h-8" />
              </Link>
            )}
          </div>

          {/* Center logo (only when back button is shown) - hidden on mobile */}
          {showBackButton && (
            <div className="hidden sm:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <img src={logo} alt="Moodshelf" className="h-8" />
            </div>
          )}

          {/* Mobile logo (only when back button is shown) */}
          {showBackButton && (
            <div className="sm:hidden flex items-center">
              <img src={logo} alt="Moodshelf" className="h-7" />
            </div>
          )}

          {/* Hamburger Menu Button (Mobile Only) */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="sm:hidden p-2 text-gray-600 hover:text-gray-900"
            aria-label="Toggle menu"
          >
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden sm:flex items-center gap-8">
            {isAuthenticated && (
              <Link 
                to="/shelf" 
                className="text-gray-600 hover:text-gray-900 text-sm font-medium font-space flex items-center gap-2"
              >
                <svg 
                  className="w-4 h-4" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                </svg>
                My Shelf
              </Link>
            )}
            
            {/* Subscription Management Button - Only for PRO users */}
            {isAuthenticated && subscription?.status === 'active' && (
              <button
                onClick={handleManageSubscription}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium font-space flex items-center gap-2"
              >
                <svg 
                  className="w-4 h-4" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Manage PRO
              </button>
            )}
            
            <LoginButton />
          </nav>
        </div>

        {/* Mobile Navigation Menu */}
        <div 
          className={`sm:hidden transition-all duration-200 ease-in-out ${
            isMenuOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
          }`}
        >
          <nav className="bg-white/95 backdrop-blur-md border-b border-purple-100/30">
            <div className="flex flex-col py-4">
              {isAuthenticated && (
                <Link 
                  to="/shelf" 
                  className="px-8 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 text-sm font-medium font-space flex items-center gap-2"
                >
                  <svg 
                    className="w-4 h-4" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                  My Shelf
                </Link>
              )}
              
              {/* Mobile Subscription Management Button */}
              {isAuthenticated && subscription?.status === 'active' && (
                <button
                  onClick={handleManageSubscription}
                  className="px-8 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 text-sm font-medium font-space flex items-center gap-2"
                >
                  <svg 
                    className="w-4 h-4" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Manage PRO
                </button>
              )}
              
              <a 
                href="mailto:hello@moodshelf.io"
                className="px-8 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 text-sm font-medium font-space"
              >
                Contact
              </a>
              
              <div className="px-8 py-3">
                <LoginButton />
              </div>
            </div>
          </nav>
        </div>
      </div>
      
      {/* Subscription Management Modal */}
      <SubscriptionManagementModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />
    </div>
  );
}

export default Navbar; 